/**
 * 정규화 함수 — 진실의 원천: harness/DATA-DICTIONARY.md §1·§2
 *
 * apps/web(브라우저) 과 apps/api(Workers) 가 이 파일을 그대로 import 해서 쓴다.
 * 두 군데에 같은 로직을 복제하지 않는다 (harness/DATA-DICTIONARY.md §2, H8 이 대조).
 *
 * 규칙 (harness/SOUL.md S1·S2 를 함수 레벨로 지킨다):
 *  - 순수 함수. 네트워크·DB 접근 없음. Web 표준 API 만 사용(프론트/백엔드 공용).
 *  - 실패해도 예외를 던지지 않는다. 정규화 불가 시 빈 값/`null` 을 반환하고
 *    원본은 항상 그대로 함께 돌려준다 — 추정해서 채우지 않는다.
 */

export interface NormalizedField {
  norm: string;
  raw: string;
}

/**
 * 시리얼 정규화: TRIM → UPPER → 내부 공백 전부 제거.
 * 원본은 raw 에 그대로 보존한다.
 * 실측 사례(harness/DATA-DICTIONARY.md NR-01): " JTM2UB0130081" → "JTM2UB0130081"
 */
export function normalizeSerial(raw: string): NormalizedField {
  const safeRaw = raw ?? "";
  const norm = safeRaw.replace(/\s+/g, "").toUpperCase();
  return { norm, raw: safeRaw };
}

/**
 * 송장번호 정규화: 숫자만 추출한다. 하이픈·문자는 버려진다.
 * 원본 문자열은 raw 에 그대로 보존한다.
 * 실측 사례(harness/DATA-DICTIONARY.md NR-02):
 *   "44910939500" → norm "44910939500"
 *   "449-3097-9074" → norm "44930979074" (원본이 다르면 정규화 후에도 다른 값 — 별개 건)
 */
export function normalizeTracking(raw: string): NormalizedField {
  const safeRaw = raw ?? "";
  const norm = safeRaw.replace(/\D+/g, "");
  return { norm, raw: safeRaw };
}

/**
 * 회사명 별칭 해석. `knownAliases` 는 `{ 별칭(trim 상태) -> 정식 회사명 }` 맵으로,
 * 호출부(apps/api)가 company_aliases 테이블(+ companies.name 자기 자신)을 읽어 준다.
 * 사전에 없는 이름은 **null** — 신규 별칭을 자동으로 만들지 않는다
 * (harness/DATA-DICTIONARY.md §1 — "신규 명칭은 대표가 직접 추가").
 */
export function resolveCompanyAlias(
  raw: string,
  knownAliases: Record<string, string>,
): string | null {
  const key = (raw ?? "").trim();
  if (key === "") return null;
  if (Object.prototype.hasOwnProperty.call(knownAliases, key)) {
    return knownAliases[key] ?? null;
  }
  return null;
}

const EXCEL_EPOCH_UTC_MS = Date.UTC(1899, 11, 30); // 엑셀 직렬값 0 = 1899-12-30
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * 날짜 정규화: 어떤 입력이든 `YYYY-MM-DD` (KST 기준) 로 맞추거나, 불가능하면 `null`.
 * 엑셀 직렬값(숫자)도 처리한다 — 엑셀 이관 시 흔한 오염(harness/DATA-DICTIONARY.md §1).
 * 실패를 추정으로 메우지 않는다 — 알 수 없으면 반드시 null.
 */
export function normalizeDate(raw: string | number | null | undefined): string | null {
  if (raw === null || raw === undefined || raw === "") return null;

  if (typeof raw === "number") {
    if (!Number.isFinite(raw)) return null;
    const ms = EXCEL_EPOCH_UTC_MS + raw * MS_PER_DAY;
    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) return null;
    return toIsoDate(d);
  }

  const trimmed = raw.trim();
  if (trimmed === "") return null;

  // 이미 YYYY-MM-DD 형태
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
  if (isoMatch) {
    const [, y, m, d] = isoMatch;
    const candidate = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d)));
    if (isValidYmd(candidate, Number(y), Number(m), Number(d))) {
      return `${y}-${m}-${d}`;
    }
    return null;
  }

  // YYYY.MM.DD / YYYY/MM/DD
  const dotMatch = /^(\d{4})[./](\d{1,2})[./](\d{1,2})/.exec(trimmed);
  if (dotMatch) {
    const y = Number(dotMatch[1]);
    const m = Number(dotMatch[2]);
    const d = Number(dotMatch[3]);
    const candidate = new Date(Date.UTC(y, m - 1, d));
    if (isValidYmd(candidate, y, m, d)) {
      return toIsoDate(candidate);
    }
    return null;
  }

  // 순수 숫자 문자열이면 엑셀 직렬값으로 취급
  if (/^\d+(\.\d+)?$/.test(trimmed)) {
    return normalizeDate(Number(trimmed));
  }

  return null;
}

function isValidYmd(d: Date, y: number, m: number, day: number): boolean {
  return d.getUTCFullYear() === y && d.getUTCMonth() === m - 1 && d.getUTCDate() === day;
}

function toIsoDate(d: Date): string {
  const y = d.getUTCFullYear().toString().padStart(4, "0");
  const m = (d.getUTCMonth() + 1).toString().padStart(2, "0");
  const day = d.getUTCDate().toString().padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * 담당자 칸에 들어온 값이 "사람 이름처럼 보이는가" 를 판단하는 **경고용 휴리스틱**이다.
 * false 를 반환해도 저장을 막지 않는다 (harness/SOUL.md S1) — UI 가 경고 표시만 한다.
 * 실측 사례(harness/DATA-DICTIONARY.md NR-03): "962로 잘못기입" → false
 */
export function looksLikePersonName(raw: string): boolean {
  const trimmed = (raw ?? "").trim();
  if (trimmed === "") return false;
  if (/\d/.test(trimmed)) return false; // 숫자가 섞이면 메모일 가능성이 크다
  const hangulName = /^[가-힣]{2,4}$/;
  const englishName = /^[A-Za-z][A-Za-z .'-]{1,39}$/;
  return hangulName.test(trimmed) || englishName.test(trimmed);
}
